
import React, { useRef, useState } from 'react';
import { complianceService } from '../../../../services/api';
import { getAppMarket } from '../../../../services/MarketService';
import { US_DEFAULT_CERT_TYPES } from '../../../../services/certificateTypes';

interface UploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    propertyId?: string;
    certificateType?: string;
    // When set, the modal runs in "attach-file" mode: it skips the expiry
    // input (the existing cert already has one), uploads the PDF, and
    // attaches it to this cert id via attachFileToCertificate -- preserving
    // the cert record, expiry, NLP scores, and audit history.
    existingCertId?: string;
    // When set, this is a "Replace" flow: create a fresh cert with new
    // expiry/file, then delete the old one after upload succeeds. Prevents
    // the old cert lingering and shadowing the new one in the health score.
    replaceCertId?: string;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onSuccess, propertyId, certificateType, existingCertId, replaceCertId }) => {
    const [expiryDate, setExpiryDate] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // New state for properties selection
    const [properties, setProperties] = useState<any[]>([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId || '');
    const [selectedType, setSelectedType] = useState(certificateType || '');

    React.useEffect(() => {
        if (isOpen && (!propertyId || !certificateType)) {
            // Fetch properties so the user can select one
            complianceService.getProperties().then((res: any) => {
                const propsData = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setProperties(propsData);
            }).catch((err: any) => {
                console.error("Failed to load properties for upload modal", err);
            });
        }
    }, [isOpen, propertyId, certificateType]);

    if (!isOpen) return null;

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];

            // Validation: File Type
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
            if (!validTypes.includes(selectedFile.type)) {
                setError("Invalid file type. Please upload PDF, JPG, or PNG.");
                return;
            }

            // Validation: Max Size (8MB - API Gateway limit)
            if (selectedFile.size > 8 * 1024 * 1024) {
                setError("File size exceeds 8MB limit.");
                return;
            }

            setError(null);

            // Compress image files
            if (selectedFile.type.startsWith('image/')) {
                try {
                    const compressedFile = await compressImage(selectedFile);
                    setFile(compressedFile);
                    console.log(`[Upload] Compressed ${selectedFile.name} from ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                } catch (error) {
                    console.error('[Upload] Compression failed, using original:', error);
                    setFile(selectedFile);
                }
            } else {
                setFile(selectedFile);
            }
        }
    };

    // Compress image function
    const compressImage = (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Resize if too large (max 2000px)
                    const maxDimension = 2000;
                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            height = (height / width) * maxDimension;
                            width = maxDimension;
                        } else {
                            width = (width / height) * maxDimension;
                            height = maxDimension;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    // Convert to blob with quality 0.8
                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                const compressedFile = new File([blob], file.name, {
                                    type: file.type,
                                    lastModified: Date.now(),
                                });
                                resolve(compressedFile);
                            } else {
                                reject(new Error('Compression failed'));
                            }
                        },
                        file.type,
                        0.3 // Quality - minimum for maximum compression
                    );
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) {
            setError("Please select a file.");
            return;
        }
        // Expiry is only required when creating a NEW cert. In attach-file
        // mode the existing cert already carries the expiry date.
        if (!existingCertId && !expiryDate) {
            setError("Please select a file and expiry date.");
            return;
        }

        const finalPropertyId = propertyId || selectedPropertyId;
        const finalCertificateType = certificateType || selectedType;

        // Check for required props
        if (!finalPropertyId || !finalCertificateType) {
            setError("Missing property information. Please try again.");
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            console.log('[Upload] Starting presigned URL upload flow...', {
                certificateType: finalCertificateType,
                propertyId: finalPropertyId,
                expiryDate,
                fileName: file.name,
                fileSize: file.size
            });

            // Step 1: Get presigned URL from backend
            const presignedResponse = await complianceService.getPresignedUploadUrl({
                fileName: file.name,
                fileType: file.type,
                propertyId: finalPropertyId
            });

            if (!presignedResponse.data?.success) {
                throw new Error(presignedResponse.data?.message || 'Failed to get upload URL');
            }

            const { uploadUrl, s3Key } = presignedResponse.data.data;
            console.log('[Upload] Got presigned URL, uploading directly to S3...', { s3Key });

            // Step 1.5: Calculate HASH for Immutable Evidence Vault (F-004)
            const calculateHash = async (file: File): Promise<string> => {
                const buffer = await file.arrayBuffer();
                const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            };

            const contentHash = await calculateHash(file);
            const uploadTimestamp = new Date().toISOString();
            console.log('[Upload] Calculated SHA-256 Hash:', contentHash);

            // Step 2: Upload directly to S3 (bypasses API Gateway!)
            const s3Response = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                },
            });

            if (!s3Response.ok) {
                throw new Error('Failed to upload file to S3');
            }
            console.log('[Upload] S3 upload successful!');

            // Step 3: Persist the file pointer.
            // In attach-file mode we update the EXISTING cert (preserving id
            // / audit / expiry) via attachFileToCertificate.
            // In normal mode we create/replace via confirmCertificateUpload.
            const confirmResponse = existingCertId
                ? await complianceService.attachFileToCertificate(existingCertId, {
                      propertyId: finalPropertyId,
                      s3Key,
                      fileName: file.name,
                      fileSize: file.size,
                      contentHash,
                  })
                : await complianceService.confirmCertificateUpload({
                      name: finalCertificateType,
                      type: finalCertificateType,
                      propertyId: finalPropertyId,
                      issuedDate: new Date().toISOString(),
                      expiryDate: expiryDate,
                      isExpired: new Date(expiryDate) < new Date(new Date().setHours(0, 0, 0, 0)),
                      s3Key: s3Key,
                      fileName: file.name,
                      fileSize: file.size,
                      contentHash, // F-004: Immutable Hash
                      uploadTimestamp // F-004: Provenance Timestamp
                  });

            if (!confirmResponse.data?.success) {
                throw new Error(confirmResponse.data?.message || 'Failed to confirm upload');
            }
            console.log('[Upload] Certificate confirmed in database!', confirmResponse.data.data);

            // Replace flow: remove the old cert now that the new one is safely
            // persisted. Failure to delete is non-fatal — the newest cert wins
            // in the calculator either way — but we log it so anyone digging
            // into duplicates knows why.
            if (replaceCertId && replaceCertId !== confirmResponse.data.data?.id) {
                try {
                    await complianceService.deleteCertificate(replaceCertId, finalPropertyId);
                    console.log('[Upload] Old cert deleted after Replace:', replaceCertId);
                } catch (delErr) {
                    console.warn('[Upload] Failed to delete old cert on Replace (non-fatal):', delErr);
                }
            }

            // Wait for parent to refresh data
            console.log('[Upload] Calling onSuccess to refresh parent data...');
            await onSuccess();
            console.log('[Upload] onSuccess completed, parent should have refreshed');

            // Brief delay to ensure UI updates
            await new Promise(resolve => setTimeout(resolve, 500));

            setIsUploading(false);
            console.log('[Upload] Closing modal');
            onClose();
        } catch (err: any) {
            console.error('[Upload] Error details:', {
                message: err.message,
                response: err.response,
                status: err.response?.status,
                data: err.response?.data
            });
            setIsUploading(false);
            if (err.response?.status === 409) {
                setError("A valid certificate of this type already exists.");
            } else if (err.response?.status === 413) {
                setError("File is too large.");
            } else {
                const errorMsg = err.response?.data?.message || err.message || "Upload failed. Please try again.";
                setError(errorMsg);
            }
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                {/* Overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal Panel */}
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                            <i className="fas fa-cloud-upload-alt text-green-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Upload Certificate
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    Upload a copy of your certificate (PDF, JPG, PNG) and set its expiry date.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-5 sm:mt-6 space-y-4">

                        {/* Property Dropdown (if not provided) */}
                        {!propertyId && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Property <span className="text-red-500">*</span></label>
                                <select
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    value={selectedPropertyId}
                                    onChange={e => setSelectedPropertyId(e.target.value)}
                                    required
                                >
                                    <option value="">Select a property</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{typeof p.address === 'object' ? p.address.line1 : p.address}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Certificate Type Dropdown (if not provided) */}
                        {!certificateType && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{getAppMarket() === 'texas' ? 'Document Type' : 'Certificate Type'} <span className="text-red-500">*</span></label>
                                <select
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                    value={selectedType}
                                    onChange={e => setSelectedType(e.target.value)}
                                    required
                                >
                                    <option value="">Select type</option>
                                    {getAppMarket() === 'texas'
                                        ? US_DEFAULT_CERT_TYPES.map((t) => (
                                            <option key={t.id} value={t.key}>{t.label}{t.required ? ' (required)' : ' (optional)'}</option>
                                        ))
                                        : <>
                                            <option value="Gas Safety">Gas Safety</option>
                                            <option value="EICR">EICR (Electrical)</option>
                                            <option value="EPC">EPC (Energy)</option>
                                            <option value="How to Rent">How to Rent Guide</option>
                                            <option value="Deposit Protection">Deposit Protection</option>
                                            <option value="Tenancy Agreement">Tenancy Agreement</option>
                                        </>}
                                </select>
                            </div>
                        )}

                        {/* File Picker */}
                        <div
                            className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="space-y-1 text-center">
                                <i className="fas fa-file-pdf text-gray-400 text-3xl mb-2"></i>
                                <div className="flex text-sm text-gray-600 justify-center">
                                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500">
                                        <span>{file ? file.name : 'Upload a file'}</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            className="sr-only"
                                            ref={fileInputRef}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">
                                    PNG, JPG, PDF up to 8MB (images auto-compressed)
                                </p>
                            </div>
                        </div>

                        {/* Expiry Date */}
                        <div>
                            <label htmlFor="expiry" className="block text-sm font-medium text-gray-700">
                                Expiry Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="expiry"
                                id="expiry"
                                required
                                className={`mt-1 block w-full border ${!expiryDate && error ? 'border-red-500' : 'border-gray-300'
                                    } rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                            />
                            {expiryDate && new Date(expiryDate) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                                <p className="mt-1 text-xs text-amber-600 font-bold">⚠️ This document is expired. It will be flagged for action.</p>
                            )}
                            {!expiryDate && (
                                <p className="mt-1 text-xs text-red-600">⚠️ Expiry date is required</p>
                            )}
                        </div>

                        {error && (
                            <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">
                                {error}
                            </div>
                        )}

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                            <button
                                type="submit"
                                disabled={isUploading}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                            >
                                {isUploading ? 'Uploading...' : 'Upload'}
                            </button>
                            <button
                                type="button"
                                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UploadModal;
